import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, OverlayTrigger, Tooltip, Spinner } from 'react-bootstrap';
import EditableRow from './EditableRow.jsx';
import rakennusKoodit from '../assets/rakennusKoodit';
import MapVisual from './MapVisual';
import MapVisualMultipleModalWrapper from './MapVisualMultipleModalWrapper.jsx';
import BuildingListRail from './BuildingListRail.jsx';
import { updateKiinteisto, updateRakennus, patchIsMainBuilding } from '../api/api.js';
import useSharePoint from "./hooks/useSharepoint.js";
import axios from "axios";
import config from "../devprodConfig"; 
import { setSharePointFolderForKiinteisto, unlinkSharePointFolder    } from "../api/api";
const API_URL = config.apiBaseUrl;

// Label above value instead of the Row/table's two columns — Perustiedot
// used to wrap badly once it moved into the 260px building rail (e.g.
// "Asunto Oy Oulun Kaijonhaka" across 3 lines in a cramped value column).
// Width-agnostic (also used for the wider report-editor column), and
// matches the same stacked label/value idiom the Rakennuksen tiedot
// modal already uses (RakennustietoRow/dl.row gy-3) for consistency.
const StackedField = ({ label, children }) => (
  <div className="stacked-field" style={{ padding: '8px 0' }}>
    <div className="text-success fw-bold" style={{ fontSize: '0.68rem' }}>{label}</div>
    <div style={{ fontSize: '0.82rem' }}>{children}</div>
  </div>
);

const PanelStacked = ({ title, children }) => (
  <div className="h-100 border rounded p-3" style={{ backgroundColor: '#fafafa' }}>
    <div className="fw-bold text-success mb-2" style={{ fontSize: '0.78rem', letterSpacing: '0.06em', textTransform: 'uppercase' }}>{title}</div>
    <style>{`
      .stacked-field-list > .stacked-field + .stacked-field { border-top: 1px solid #e5e5ea; }
      /* EditableRow's value sits one level deeper now (inside its own
         click-to-edit wrapper div, see EditableRow.jsx) — the label
         already carries the contrast (accent color + bold), so the
         value stays medium-weight rather than bold for a quieter line. */
      .stacked-field dd span:first-of-type { font-weight: 500; font-size: 0.82rem; }
    `}</style>
    <div className="stacked-field-list" style={{ display: 'flex', flexDirection: 'column' }}>{children}</div>
  </div>
);

const FIELDS = ['kokonaisala', 'kerrosala', 'huoneistoala', 'tilavuus', 'kerroksia', 'kellarikerroksia'];

export default function KiinteistoDetails({ card, setCard, editable = true, hideSharePoint = false, showMap = false }) {
  const [activeField, setActiveField] = useState(FIELDS[0]);
  const [showModal, setShowModal] = useState(false);
  // Shared by the page-wide building rail and the map — picking a
  // building in either one highlights it in both, so they need one
  // piece of state, not two that could drift apart.
  const [highlightedKey, setHighlightedKey] = useState(null);
  const [selectedRakennus, setSelectedRakennus] = useState(null);
  const [localBuildings, setLocalBuildings] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [currentFolderName, setCurrentFolderName] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [successMessage, setSuccessMessage] = useState(null);
const [uploading, setUploading] = useState(false);
const [showConfirmModal, setShowConfirmModal] = useState(false);
const [selectedFolder, setSelectedFolder] = useState(null);
  const prevCardRef = React.useRef(card);
const {
  files,
  loadingSP,
  spFolder,
  currentFolderId,
  setCurrentFolderId,
  folderStack,
  setFolderStack,
  setFiles ,
  isFallback
} = useSharePoint(card);
const isRoot = !folderStack.length;

useEffect(() => {
  
  if (spFolder?.name && !currentFolderName) {
    setCurrentFolderName(spFolder.name);
  }
}, [spFolder, currentFolderName]);
const handleSetFolder = async () => {
  const folder = {
    id: currentFolderId || spFolder?.id,
    name: currentFolderName || spFolder?.name || "Valittu kansio"
  };

  if (!folder.id) {
    console.error("❌ Folder missing:", folder);
    return;
  }

  const confirmed = window.confirm(
    `Haluatko valita tämän kansion?\n\n${folder.name}`
  );

  if (!confirmed) return;

  try {
    console.log("📌 FRONTEND SET FOLDER:", {
      kiinteistoId: card.id_kiinteisto,
      folderId: folder.id,
      folderName: folder.name
    });

    await setSharePointFolderForKiinteisto(
      card.id_kiinteisto,
      folder
    );

    console.log("✅ Folder linked:", folder.name);

    window.location.reload();

  } catch (err) {
    console.error(
      "❌ Folder linking failed:",
      err.response?.data || err
    );
  }
};
const handleUnlinkFolder = async () => {
  try {
    await unlinkSharePointFolder(card.id_kiinteisto);

    setSuccessMessage("SharePoint-kansio linkitys poistettu onnistuneesti");

    // reset UI state
    setCurrentFolderId(null);

    setTimeout(() => {
      window.location.reload();
    }, 2000);

  } catch (err) {
    console.error(err);
    alert("❌ Poisto epäonnistui");
  }
};
const uploadFile = async (file) => {
  try {
    const folderId = currentFolderId || spFolder?.id;

    if (!folderId) {
      alert("❌ Ei kansiota valittuna");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("folderId", folderId);
    formData.append("isReportUpload", false);

    setUploading(true);
    setUploadProgress(0);

    await axios.post(`${API_URL}/sharepoint/upload`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
      withCredentials: true,

    
      onUploadProgress: (progressEvent) => {
        const percent = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        setUploadProgress(percent);
      }
    });

    alert("✅ Tiedosto ladattu!");

  } catch (err) {
    console.error(err);
    alert("❌ Upload epäonnistui");
  } finally {
    setUploading(false);
    setUploadProgress(0);
  }

 
  try {
    const folderId = currentFolderId || spFolder?.id;

    const res = await axios.get(`${API_URL}/sharepoint/files`, {
      params: { folderId },
      withCredentials: true
    });

    setFiles(res.data);

  } catch (err) {
    console.warn("Refresh failed:", err);
  }
};
const handleDragOver = (e) => {
  e.preventDefault();
  setDragActive(true);
};

const handleDragLeave = (e) => {
  e.preventDefault();
  setDragActive(false);
};

const handleDrop = async (e) => {
  e.preventDefault();
  setDragActive(false);

 const files = Array.from(e.dataTransfer.files);
if (!files.length) return;

for (const file of files) {
  await uploadFile(file);
}
};
const handleUploadFile = async (e) => {
  const files = Array.from(e.target.files);
  if (!files.length) return;

  for (const file of files) {
    await uploadFile(file); 
  }
};

  React.useEffect(() => {
    if (card !== prevCardRef.current) {
      prevCardRef.current = card;
      setLocalBuildings(null);
    }
  }, [card]);

  if (!card) return null;

  const allBuildings = localBuildings ?? (card?.rakennukset_fulls || []);
  const mainBuildings = allBuildings.filter(r => r.isMainBuilding);

  const handleOpenModal = (rakennus) => {
    setSelectedRakennus(rakennus);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedRakennus(null);
  };

  const handleToggleMainBuilding = async (rakennus) => {
    const newValue = !rakennus.isMainBuilding;
    const updated = allBuildings.map(r =>
      r.id_rakennus === rakennus.id_rakennus ? { ...r, isMainBuilding: newValue } : r
    );
    setLocalBuildings(updated);
    try {
      await patchIsMainBuilding(rakennus.id_rakennus, { value: newValue });
      if (setCard) setCard({ ...card, rakennukset_fulls: updated });
    } catch (err) {
      console.error('Failed to toggle main building', err);
      setLocalBuildings(null);
    }
  };

  const sumField = (field) =>
    mainBuildings.reduce((sum, r) => sum + (parseFloat(r[field]) || 0), 0);

  const visualizeRange = (arr, alwaysFromZero = false) => {
    if (!arr?.length) return '—';
    const min = Math.min(...arr);
    const max = Math.max(...arr);
    if (alwaysFromZero) return max === 0 ? '0' : `0–${max}`;
    return min === max ? min : `${min}–${max}`;
  };

  const unit = (f) =>
    ['kokonaisala', 'kerrosala', 'huoneistoala'].includes(f) ? ' m²' : f === 'tilavuus' ? ' m³' : '';

  const rakennus0 = card?.rakennukset_fulls?.[0];
  const missingFields = [
    { label: 'Kohteen nimi',        value: card?.kohteen_nimi },
    { label: 'Tontin omistaja',     value: card?.tontin_omistaja },
    { label: 'Osoite',              value: rakennus0?.osoite },
    { label: 'Toimipaikka',         value: rakennus0?.toimipaikka },
    { label: 'Postinumero',         value: rakennus0?.postinumero },
    { label: 'Kiinteistötunnus',    value: card?.kiinteistotunnus },
    { label: 'Tontin koko m²',      value: card?.tontin_koko },
    { label: 'Rakennusvuosi',       value: rakennus0?.rakennusvuosi },
    { label: 'Peruskorjausvuosi',   value: rakennus0?.peruskorjausvuosi },
    { label: 'Laajennusvuosi',      value: rakennus0?.laajennusvuosi },
    { label: 'Rakennusten lkm',     value: card?.rakennusten_lkm },
    { label: 'Ulkorakennusten lkm', value: card?.ulkorakennusten_lkm },
    { label: 'Huoneistojen lkm',    value: card?.huoneistojen_lkm },
    { label: 'Kokonaisala (m²)',    value: rakennus0?.kokonaisala },
  ].filter(f => !f.value).map(f => f.label);
const isLinked = !isFallback && !loadingSP;

  // Extracted so Perustiedot can render inside the building rail (below
  // the building rows) instead of its usual row g-3 column, when showMap.
  const perustiedotPanel = (
    <PanelStacked title="Perustiedot">
      <StackedField label="Kohteen nimi">
        <EditableRow item={card} field="kohteen_nimi" idField="id_kiinteisto" updateFn={updateKiinteisto} editable={editable} showSource />
      </StackedField>
      <StackedField label="Tontin omistaja">
        <EditableRow item={card} field="tontin_omistaja" idField="id_kiinteisto" updateFn={updateKiinteisto} editable={editable} showSource />
      </StackedField>
      <StackedField label="Osoite">
        <EditableRow
          item={card.mergedOsoite
            ? { osoite: card.mergedOsoite, metadata: { osoite: { source: card.metadata?.mergedOsoite?.source } } }
            : card?.rakennukset_fulls?.[0]}
          field="osoite" idField="id_rakennus" updateFn={updateRakennus}
          editable={editable && !card.mergedOsoite} showSource
        />
      </StackedField>
      <StackedField label="Toimipaikka">
        <EditableRow item={card?.rakennukset_fulls?.[0]} field="toimipaikka" idField="id_rakennus" updateFn={updateRakennus} editable={editable} showSource />
      </StackedField>
      <StackedField label="Postinumero">
        <EditableRow item={card?.rakennukset_fulls?.[0]} field="postinumero" idField="id_rakennus" updateFn={updateRakennus} editable={editable} showSource />
      </StackedField>
      <StackedField label="Kiinteistötunnus">
        <EditableRow item={card} field="kiinteistotunnus" idField="id_kiinteisto" updateFn={updateKiinteisto} showSource editable={editable} />
      </StackedField>
      <StackedField label="Tontin koko m²">
        <EditableRow item={card} field="tontin_koko" idField="id_kiinteisto" updateFn={updateKiinteisto} showSource editable={editable} />
      </StackedField>
      <StackedField label="Rakennusvuosi">
        <EditableRow item={card?.rakennukset_fulls?.[0]} field="rakennusvuosi" idField="id_rakennus" updateFn={updateRakennus} editable={editable} showSource />
      </StackedField>
      <StackedField label="Peruskorjausvuosi">
        <EditableRow item={card?.rakennukset_fulls?.[0]} field="peruskorjausvuosi" idField="id_rakennus" updateFn={updateRakennus} editable={editable} showSource />
      </StackedField>
      <StackedField label="Laajennusvuosi">
        <EditableRow item={card?.rakennukset_fulls?.[0]} field="laajennusvuosi" idField="id_rakennus" updateFn={updateRakennus} editable={editable} showSource />
      </StackedField>
      <StackedField label="Rakennusten lkm">
        <EditableRow item={card} field="rakennusten_lkm" idField="id_kiinteisto" updateFn={updateKiinteisto} editable={editable} showSource />
      </StackedField>
      <StackedField label="Ulkorakennusten lkm">
        <EditableRow item={card} field="ulkorakennusten_lkm" idField="id_kiinteisto" updateFn={updateKiinteisto} editable={editable} showSource />
      </StackedField>
      <StackedField label="Huoneistojen lkm">
        <EditableRow item={card} field="huoneistojen_lkm" idField="id_kiinteisto" updateFn={updateKiinteisto} editable={editable} showSource />
      </StackedField>
    </PanelStacked>
  );

  // Extracted so SharePoint docs / Rakennukset table / Measurements panel
  // are defined once and reused by both the desktop two-column layout and
  // the mobile CSS-grid stack (kiinteisto-rail-grid below) instead of
  // duplicating this large block per layout.
  const restContent = (
    <>
{!hideSharePoint && <div className="mt-4">
  <div
  onDragOver={handleDragOver}
  onDragLeave={handleDragLeave}
  onDrop={handleDrop}
  style={{
    border: dragActive ? "2px dashed #198754" : "2px dashed transparent",
    background: dragActive ? "#e9f7ef" : "transparent",
    transition: "all 0.2s ease",
    padding: "8px",
    borderRadius: "6px"
  }}
>
  {successMessage && (
  <div
    className="alert alert-success alert-dismissible fade show d-flex align-items-center gap-2"
    role="alert"
    style={{
      borderRadius: "10px",
      boxShadow: "0 4px 12px rgba(0,0,0,0.1)"
    }}
  >
    <i className="bi bi-check-circle-fill fs-5"></i>

    <div className="flex-grow-1">
      {successMessage}
    </div>

    <button
      type="button"
      className="btn-close"
      onClick={() => setSuccessMessage(null)}
    />
  </div>
)}
<h5 className="d-flex justify-content-between align-items-center">
  <span>📂 SharePoint dokumentit</span>
</h5>

{loadingSP ? (
  <div className="d-flex align-items-center gap-2 mt-2 text-muted small">
    <Spinner animation="border" size="sm" />
    Tarkistetaan SharePoint-kansiota...
  </div>
) : (
  <div
    className="d-flex align-items-center justify-content-between px-3 py-2 rounded mt-2"
    style={{
      background: isLinked ? "#f1f8f4" : "#fff8e1",
      border: `1px solid ${isLinked ? "#c8e6c9" : "#ffe082"}`
    }}
  >
    {/* LEFT */}
    <div className="d-flex align-items-center gap-2">
      <span style={{ fontSize: "18px" }}>
        {isLinked ? "📂" : "⚠️"}
      </span>

      <span style={{ fontSize: "0.9rem", fontWeight: 500 }}>
        {isLinked
          ? currentFolderName || "SharePoint-kansio linkitetty"
          : "Tälle kiinteistölle ei ole vielä asetettu kansiota"}
      </span>
    </div>

    
    <div className="d-flex gap-2">
      {!isLinked ? (
        <button
          className="btn btn-sm btn-success"
          onClick={handleSetFolder}
        >
          Aseta tämä kansio
        </button>
      ) : (
        <button
  className="btn btn-sm btn-outline-danger"
  onClick={() => {
    const ok = window.confirm("Haluatko varmasti poistaa SharePoint-linkityksen?");
    if (ok) handleUnlinkFolder();
  }}
>
  Poista
</button>
      )}

      <button
        className="btn btn-sm btn-outline-secondary"
        onClick={() => {
          const folderId = currentFolderId || spFolder?.id;
          if (!folderId) return;

          const safeName =
            card?.kohteen_nimi ||
            spFolder?.name ||
            "Kiinteistö";

          window.open(
            `/sharepoint?folderId=${folderId}&name=${encodeURIComponent(safeName)}`,
            "_blank"
          );
        }}
      >
        ↗
      </button>
    </div>
  </div>
)}

  {/* 📤 Upload button */}
  <div className="d-flex gap-2 mb-2">


 <input
  id="fileUploadInput"
  type="file"
  multiple   
  style={{ display: "none" }}
  onChange={handleUploadFile}
/>
  </div>

  
  {dragActive && (
    <div className="text-center text-success mb-2">
      📦 Pudota tiedosto tähän
    </div>
  )}
{uploading && (
  <div className="mb-2">
    <div className="small text-muted">
      Ladataan... {uploadProgress}%
    </div>

    <div className="progress" style={{ height: "6px" }}>
      <div
        className="progress-bar bg-success"
        role="progressbar"
        style={{ width: `${uploadProgress}%` }}
      />
    </div>
  </div>
)}
<div className="d-flex gap-2 mb-2 align-items-center">

  {/* ⬅️ BACK */}
  {folderStack.length > 0 && (
    <Button
      variant="outline-secondary"
      size="sm"
     onClick={() => {
  setFolderStack(prev => {
    const newStack = [...prev];
    const previousFolderId = newStack.pop();

    
    setCurrentFolderId(previousFolderId || null);

    
    setCurrentFolderName(null);

    return newStack;
  });
}}
    >
      ⬅️ Takaisin
    </Button>
  )}

  {/* 📤 UPLOAD */}
{!isRoot && (
  <Button
    variant="outline-secondary"
    size="sm"
    onClick={() => document.getElementById("fileUploadInput").click()}
  >
    📤 Lataa tiedosto
  </Button>
)}

  
  <div className="ms-auto small text-muted">
    {currentFolderId ? "" : "Juuri"}
  </div>

</div>

  {/* 📂 FILE LIST */}
  {loadingSP ? (
    <div className="text-center my-4">
      <Spinner animation="border" variant="success" />
      <div className="mt-2 text-muted">
        Ladataan tiedostoja...
      </div>
    </div>
  ) : !spFolder && !isFallback ? (
  <p className="text-muted">Ei linkitettyä SharePoint-kansiota</p>
  ) : files.length === 0 ? (
    <p className="text-muted">Ei tiedostoja kansiossa</p>
  ) : (
    <div className="sharepoint-modal-list">
  {files.map((file) => (
    <div
      key={file.id}
      className="sharepoint-modal-card"
      onClick={() => {
        if (file.folder) {
          setFolderStack(prev => [
            ...prev,
            currentFolderId || spFolder?.id
          ]);
          setCurrentFolderId(file.id);
          setCurrentFolderName(file.name);
        } else {
          window.open(file.webUrl, "_blank");
        }
      }}
    >
      <div className="sharepoint-modal-file-info">
        <div className="sharepoint-modal-icon">
          {file.folder ? "📁" : "📄"}
        </div>

        <div className="flex-grow-1">
          <div className="sharepoint-modal-name">
            {file.name}
          </div>

      <div className="sharepoint-modal-meta">
  {file.folder ? (
    <>
      Kansio
      {file.folder.childCount !== undefined && (
        <> - {file.folder.childCount} tiedostoa</>
      )}
    </>
  ) : (
    file.size
      ? `${Math.round(file.size / 1024)} KB`
      : "Tiedosto"
  )}
</div>
        </div>

        {!file.folder && (
          <button
            type="button"
            className="btn btn-sm btn-outline-success"
            onClick={(e) => {
              e.stopPropagation();
              window.open(file.webUrl, "_blank");
            }}
          >
            Avaa
          </button>
        )}
      </div>
    </div>
  ))}
</div>
  )}
</div>

</div>}
          {/* Buildings listing panel — redundant on the landing page now
              that the map's own Rakennukset sidebar shows the same info
              (tunnus, luokitus, Ensisijainen toggle, Tiedot). Still
              needed in PropertyInfoAccordion's reuse of this component
              (report editor), which has no map/showMap. */}
          {!showMap && allBuildings.length > 0 && (
            <div className="border rounded p-3" style={{ backgroundColor: '#fafafa' }}>
              <div className="fw-bold text-success mb-2" style={{ fontSize: '0.78rem', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Rakennukset</div>

              {/* Mobile: card list */}
              <div className="d-flex d-md-none flex-column gap-2">
                {allBuildings.map((rakennus, idx) => (
                  <div key={rakennus.rakennustunnus || idx} className="border rounded p-2 bg-white d-flex justify-content-between align-items-center gap-2">
                    <div style={{ fontSize: '0.8rem' }}>
                      <div className="fw-semibold">{rakennus?.rakennustunnus || '—'}</div>
                      <div className="text-muted">{rakennus?.rakennusluokitus || '—'}</div>
                    </div>
                    <div className="d-flex align-items-center gap-2 flex-shrink-0">
                      <OverlayTrigger placement="top" overlay={<Tooltip>Ensisijaiset rakennukset näytetään kartoissa ja laskelmissa</Tooltip>}>
                        <i className="bi bi-info-circle text-muted" style={{ cursor: 'default', fontSize: '0.75rem' }} />
                      </OverlayTrigger>
                      <Form.Check
                        type="switch"
                        id={`main-switch-mob-${rakennus.id_rakennus}`}
                        checked={rakennus.isMainBuilding}
                        onChange={() => handleToggleMainBuilding(rakennus)}
                        className="m-0"
                        style={{ fontSize: '0.75rem' }}
                      />
                      <Button size="sm" variant="success" style={{ fontSize: '0.75rem', padding: '2px 8px' }} onClick={() => handleOpenModal(rakennus)}>
                        Tiedot
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop: table */}
              <div className="d-none d-md-block table-responsive">
                <table className="table table-sm mb-0" style={{ borderCollapse: 'separate', borderSpacing: '0 0.25rem' }}>
                  <thead>
                    <tr>
                      <th style={{ fontSize: '0.78rem' }}>Rakennustunnus</th>
                      <th style={{ fontSize: '0.78rem' }}>Rakennusluokitus</th>
                      <th style={{ fontSize: '0.78rem' }}>
                        Ensisijainen{' '}
                        <OverlayTrigger placement="top" overlay={<Tooltip>Ensisijaiset rakennukset näytetään kartoissa ja laskelmissa</Tooltip>}>
                          <i className="bi bi-info-circle text-muted" style={{ cursor: 'default', fontSize: '0.75rem' }} />
                        </OverlayTrigger>
                      </th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {allBuildings.map((rakennus, idx) => (
                      <tr key={rakennus.rakennustunnus || idx}>
                        <td className="fw-semibold" style={{ verticalAlign: 'middle', fontSize: '0.82rem' }}>{rakennus?.rakennustunnus || '—'}</td>
                        <td style={{ verticalAlign: 'middle', fontSize: '0.82rem' }}>{rakennus?.rakennusluokitus || '—'}</td>
                        <td style={{ verticalAlign: 'middle' }}>
                          <Form.Check
                            type="switch"
                            id={`main-switch-${rakennus.id_rakennus}`}
                            checked={rakennus.isMainBuilding}
                            onChange={() => handleToggleMainBuilding(rakennus)}
                            className="m-0"
                          />
                        </td>
                        <td style={{ verticalAlign: 'middle' }}>
                          <Button size="sm" variant="success" onClick={() => handleOpenModal(rakennus)}>Tiedot</Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Measurements panel */}
          <div className="border rounded p-3" style={{ backgroundColor: '#fafafa' }}>
            <div className="fw-bold text-success mb-2" style={{ fontSize: '0.78rem', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Mitat ja kerrokset</div>

            {/* Field tabs */}
            <div className="d-flex flex-wrap gap-1 mb-3">
              {FIELDS.map(f => (
                <button
                  key={f}
                  onClick={() => setActiveField(f)}
                  className={`btn btn-sm ${activeField === f ? 'btn-success' : 'btn-outline-secondary'}`}
                >
                  {f.charAt(0).toUpperCase() + f.slice(1)}{unit(f)}
                </button>
              ))}
            </div>

            {/* Building rows */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '4px' }}>
              {mainBuildings.map((b, i) => (
                <React.Fragment key={`${i}-${activeField}`}>
                  <div className="border rounded px-2 py-1" style={{ backgroundColor: '#f8f8f8', minWidth: 0 }}>
                    <div className="fw-semibold" style={{ fontSize: '0.82rem' }}>{b.rakennustunnus || '—'}</div>
                    {b.rakennusluokitus && <div className="text-muted" style={{ fontSize: '0.82rem' }}>{b.rakennusluokitus}</div>}
                  </div>
                  <div className="border rounded px-2 py-1 d-flex align-items-center" style={{ backgroundColor: '#f8f8f8' }}>
                    <EditableRow item={b} field={activeField} idField="id_rakennus" updateFn={updateRakennus} editable={editable} showSource fontSize="0.82rem" />
                  </div>
                </React.Fragment>
              ))}
              <div className="border rounded px-2 py-1" style={{ backgroundColor: 'rgba(4,170,0,0.1)', color: '#027000', fontSize: '0.82rem', fontWeight: 600 }}>
                Yht.
              </div>
              <div className="border rounded px-2 py-1 d-flex align-items-center" style={{ backgroundColor: 'rgba(4,170,0,0.1)', color: '#027000', fontWeight: 600, fontSize: '0.82rem' }}>
                {['kokonaisala', 'kerrosala', 'huoneistoala', 'tilavuus'].includes(activeField)
                  ? sumField(activeField) + unit(activeField)
                  : visualizeRange(mainBuildings.map(r => r[activeField] || 0), activeField === 'kellarikerroksia')}
              </div>
            </div>
          </div>
    </>
  );

  const missingFieldsBox = missingFields.length > 0 ? (
    <>
      <i className="bi bi-exclamation-circle-fill text-danger mt-1" style={{ flexShrink: 0 }} />
      <div>
        <div className="fw-semibold text-danger" style={{ fontSize: '0.85rem' }}>
          Puuttuvat tiedot
        </div>
        <div className="text-danger" style={{ fontSize: '0.82rem' }}>
          {missingFields.join(', ')}
        </div>
      </div>
    </>
  ) : null;

  return (
    <div className="mt-2 px-2 px-md-4">
      {showMap ? (
        // Mobile: map first, then the building rail, Perustiedot,
        // missing-fields warning and the rest, all stacked full-width.
        // Desktop (md+): 260px rail (buildings+Perustiedot) beside an
        // independent map/missing/rest column. A CSS grid with shared
        // rows between the two columns (tried first) forced buildings'
        // row to match the map's height and Perustiedot's row-span to
        // inflate the missing-fields box — rail-group and content-col
        // are two height-independent flex stacks instead, same as the
        // original (pre-mobile-fix) layout; content-col is only
        // `display: contents` on mobile, which dissolves its own box
        // and promotes map/missing/rest to flat siblings of rail-group
        // so `order` alone gives the map-first mobile sequence.
        <div className="kiinteisto-rail-flex">
          <style>{`
            .kiinteisto-rail-flex { display: flex; flex-direction: column; gap: 16px; }
            .kiinteisto-rail-flex .rail-group { order: 2; display: flex; flex-direction: column; gap: 12px; }
            .kiinteisto-rail-flex .content-col { display: contents; }
            .kiinteisto-rail-flex .map-item { order: 1; }
            .kiinteisto-rail-flex .missing-item { order: 3; }
            .kiinteisto-rail-flex .rest-item { order: 4; }
            @media (min-width: 768px) {
              .kiinteisto-rail-flex { flex-direction: row; align-items: flex-start; }
              .kiinteisto-rail-flex .rail-group { width: 260px; flex-shrink: 0; order: 0; }
              .kiinteisto-rail-flex .content-col { display: flex; flex-direction: column; gap: 16px; flex: 1 1 0%; min-width: 0; }
            }
          `}</style>
          <div className="rail-group">
            <BuildingListRail allBuildings={allBuildings} highlightedKey={highlightedKey} setHighlightedKey={setHighlightedKey}
              onSelectBuilding={handleOpenModal} onToggleMainBuilding={handleToggleMainBuilding} maxHeight="50vh" />
            {perustiedotPanel}
          </div>
          <div className="content-col">
            <div className="map-item">
              <MapVisualMultipleModalWrapper onSelectBuilding={handleOpenModal} onToggleMainBuilding={handleToggleMainBuilding}
                allBuildings={allBuildings} highlightedKey={highlightedKey} setHighlightedKey={setHighlightedKey}
                showSidebar={false} mapHeight="320px" />
            </div>
            {missingFieldsBox && (
              <div className="missing-item d-flex align-items-start gap-2 rounded p-2"
                style={{ backgroundColor: '#fff5f5', border: '1px solid #f5c2c7' }}>
                {missingFieldsBox}
              </div>
            )}
            <div className="rest-item d-flex flex-column gap-3">
              {restContent}
            </div>
          </div>
        </div>
      ) : (
        <>
          {missingFieldsBox && (
            <div className="d-flex align-items-start gap-2 rounded p-2 mb-3"
              style={{ backgroundColor: '#fff5f5', border: '1px solid #f5c2c7' }}>
              {missingFieldsBox}
            </div>
          )}
          <div className="row g-3">
            <div className="col-12 col-md-6">
              {perustiedotPanel}
            </div>
            <div className="col-12 col-md-6 d-flex flex-column gap-3">
              {restContent}
            </div>
          </div>
        </>
      )}

      {/* Rakennus detail modal */}
      <Modal
        show={showModal}
        onHide={handleCloseModal}
        centered
        fullscreen="md-down"
        size="xl"
        contentClassName="border-success rounded-3"
      >
        <Modal.Header closeButton className="bg-success bg-opacity-10 border-success">
          <Modal.Title className="text-success">Rakennuksen tiedot</Modal.Title>
        </Modal.Header>

        <Modal.Body className="px-4 py-3">
          {selectedRakennus ? (
            <>
              {/* Four field groups as cards, 2 per row, instead of one
                  narrow dt/dd column stretched across the whole xl modal
                  (lots of empty space to the right of every value). The
                  dl/dt/dd structure is unchanged (EditableRow renders it
                  directly) — just no longer wrapped in a Bootstrap .row,
                  so its col-sm-3 width class is neutralized by
                  rakennus-card-dl below, stacking label above value
                  instead of side by side. Fields use EditableRow (not
                  RakennustietoRow, which still shows an always-visible
                  pencil elsewhere) for the same no-pencil/hover-to-edit
                  style as the Perustiedot panel. */}
              <style>{`
                .rakennus-card-dl dt, .rakennus-card-dl dd {
                  width: 100% !important;
                  max-width: 100%;
                  white-space: normal !important;
                }
                .rakennus-card-dl dt {
                  font-size: 0.78rem;
                  font-weight: 600;
                  color: #6c757d;
                  margin-top: 10px;
                }
                .rakennus-card-dl dt:first-child { margin-top: 0; }
                .rakennus-card-dl dd { margin-bottom: 0; }
              `}</style>
              {selectedRakennus?.sijainti?.coordinates?.[1] && (
                <div className="mb-3">
                  <MapVisual
                    coords={[selectedRakennus.sijainti.coordinates[1], selectedRakennus.sijainti.coordinates[0]]}
                    height="220px"
                  />
                </div>
              )}
              <div className="row g-3">
                <div className="col-12 col-md-6">
                  <div className="border rounded-3 p-3" style={{ backgroundColor: '#fafafa' }}>
                    <dl className="rakennus-card-dl mb-0">
                      <EditableRow otsikko="Rakennustunnus" item={selectedRakennus} field="rakennustunnus" idField="id_rakennus" updateFn={updateRakennus} editable showSource />
                      <EditableRow otsikko="Osoite" item={selectedRakennus} field="osoite" idField="id_rakennus" updateFn={updateRakennus} editable showSource />
                      <EditableRow otsikko="Toimipaikka" item={selectedRakennus} field="toimipaikka" idField="id_rakennus" updateFn={updateRakennus} editable showSource />
                      <EditableRow otsikko="Postinumero" item={selectedRakennus} field="postinumero" idField="id_rakennus" updateFn={updateRakennus} editable showSource />
                    </dl>
                  </div>
                </div>

                <div className="col-12 col-md-6">
                  <div className="border rounded-3 p-3" style={{ backgroundColor: '#fafafa' }}>
                    <dl className="rakennus-card-dl mb-0">
                      <EditableRow otsikko="Rakennusluokitus" item={selectedRakennus} field="rakennusluokitus" idField="id_rakennus" updateFn={updateRakennus} editable showSource options={rakennusKoodit.rakennusluokitus} />
                      <EditableRow otsikko="Rakennusvuosi" item={selectedRakennus} field="rakennusvuosi" idField="id_rakennus" updateFn={updateRakennus} editable showSource />
                      <EditableRow otsikko="Peruskorjauvuosi" item={selectedRakennus} field="peruskorjausvuosi" idField="id_rakennus" updateFn={updateRakennus} editable showSource />
                      <EditableRow otsikko="Laajennusvuosi" item={selectedRakennus} field="laajennusvuosi" idField="id_rakennus" updateFn={updateRakennus} editable showSource />
                    </dl>
                  </div>
                </div>

                <div className="col-12 col-md-6">
                  <div className="border rounded-3 p-3" style={{ backgroundColor: '#fafafa' }}>
                    <dl className="rakennus-card-dl mb-0">
                      <EditableRow otsikko="Kokonaisala (m²)" item={selectedRakennus} field="kokonaisala" idField="id_rakennus" updateFn={updateRakennus} editable showSource />
                      <EditableRow otsikko="Kerrosala (m²)" item={selectedRakennus} field="kerrosala" idField="id_rakennus" updateFn={updateRakennus} editable showSource />
                      <EditableRow otsikko="Huoneistoala (m²)" item={selectedRakennus} field="huoneistoala" idField="id_rakennus" updateFn={updateRakennus} editable showSource />
                      <EditableRow otsikko="Tilavuus (m³)" item={selectedRakennus} field="tilavuus" idField="id_rakennus" updateFn={updateRakennus} editable showSource />
                      <EditableRow otsikko="Kerroksia" item={selectedRakennus} field="kerroksia" idField="id_rakennus" updateFn={updateRakennus} editable showSource />
                      <EditableRow otsikko="Huoneistojen lukumäärä" item={selectedRakennus} field="huoneistoja" idField="id_rakennus" updateFn={updateRakennus} editable showSource />
                    </dl>
                  </div>
                </div>

                <div className="col-12 col-md-6">
                  <div className="border rounded-3 p-3" style={{ backgroundColor: '#fafafa' }}>
                    <dl className="rakennus-card-dl mb-0">
                      <EditableRow otsikko="Runkotapa" item={selectedRakennus} field="runkotapa" idField="id_rakennus" updateFn={updateRakennus} editable showSource options={rakennusKoodit.rakentamistapa} />
                      <EditableRow otsikko="Käyttötilanne" item={selectedRakennus} field="kayttotilanne" idField="id_rakennus" updateFn={updateRakennus} editable showSource options={rakennusKoodit.kayttotilanne} />
                      <EditableRow otsikko="Julkisivumateriaali" item={selectedRakennus} field="julkisivumateriaali" idField="id_rakennus" updateFn={updateRakennus} editable showSource options={rakennusKoodit.julkisivumateriaali} />
                      <EditableRow otsikko="Lämmitystapa" item={selectedRakennus} field="lammitystapa" idField="id_rakennus" updateFn={updateRakennus} editable showSource options={rakennusKoodit.lammitystapa} />
                      <EditableRow otsikko="Lämmitysenergialähde" item={selectedRakennus} field="lammitysenergialahde" idField="id_rakennus" updateFn={updateRakennus} editable showSource options={rakennusKoodit.lammitysenergialahde} />
                      <EditableRow otsikko="Rakennusaine" item={selectedRakennus} field="rakennusaine" idField="id_rakennus" updateFn={updateRakennus} editable showSource options={rakennusKoodit.rakennusaine} />
                    </dl>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <p>Ei tietoja</p>
          )}
        </Modal.Body>

        <Modal.Footer className="bg-light">
          <Button variant="secondary" onClick={handleCloseModal}>Sulje</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
